import { DefaultPlatformFormation, FormationConfig, FormationConfigMap, PlatformFormation, PositionCoordinates } from '../types/formation';
import { TeamPlayers } from '../types/team';
import { platformPositionToSortOrder } from '../static/constants/positions';
import { PlatformPosition } from '../types/position';
import { defaultFormationConfigMap, defaultFormationToSortOrder } from '../static/constants/formations';
import { stringArraysAreEqual } from './commonUtils';


// get all formations for the given tenant, being the set of unique formations in the defaultFormationConfigMap and formationConfigs
export const getFormationOptions = (formationConfigs: FormationConfigMap | undefined) => {
  const formations = Object.keys(defaultFormationConfigMap);
  formations.sort((a, b) => defaultFormationToSortOrder[b as DefaultPlatformFormation] - defaultFormationToSortOrder[a as DefaultPlatformFormation]);

  Object.keys(formationConfigs ?? {}).forEach(formation => {
    if (!defaultFormationConfigMap[formation]) {
      formations.unshift(formation);
    }
  });

  return formations;
};


// get the formation config for the given formation
export const getFormationConfig = (formation: PlatformFormation, formationConfigs: FormationConfigMap | undefined) => {
  return formationConfigs?.[formation] ?? defaultFormationConfigMap[formation] ?? {};
};


// get the sorted positions of the given formation config
export const getPositionsOfFormationConfig = (formationConfig: FormationConfig): PlatformPosition[] => {
  const positions = Object.keys(formationConfig) as PlatformPosition[];
  return positions.sort((a, b) => platformPositionToSortOrder[a] - platformPositionToSortOrder[b]);
};


export const formationConfigsAreEqual = (formationConfig1: FormationConfig, formationConfig2: FormationConfig) => {
  const sortedPositions1 = getPositionsOfFormationConfig(formationConfig1);
  const sortedPositions2 = getPositionsOfFormationConfig(formationConfig2);

  if (!stringArraysAreEqual(sortedPositions1, sortedPositions2)) {
    return false;
  }

  for (const position of sortedPositions1) {
    const coordinates1 = formationConfig1[position];
    const coordinates2 = formationConfig2[position];

    if (coordinates1 && coordinates2) {
      const coordinatesAreEqual = coordinates1.x === coordinates2.x && coordinates1.y === coordinates2.y;
      if (!coordinatesAreEqual) {
        return false;
      }
    }
  }

  return true;
};


// formation config has typical backline if it has a goalkeeper in the bottom 2 coordinate rows, and no other positions are in the bottom 3 coordinate rows
export const formationConfigHasTypicalBackline = (formationConfig: FormationConfig) => {
  const formationHasGoalkeeperInBottom2Rows = Object.entries(formationConfig).some(
    ([position, coordinate]) => (position === 'GK' && coordinate.y >= 15)
  );

  const formationHasNoOtherPositionsInBottom3Rows = Object.entries(formationConfig).every(
    ([position, coordinate]) => (position === 'GK' || coordinate.y < 15)
  );

  return formationHasGoalkeeperInBottom2Rows && formationHasNoOtherPositionsInBottom3Rows;
};


// 'LS'
// 'S'
// 'RS'
// 'LW'
// 'RW'
// 'LCAM'
// 'CAM'
// 'RCAM'
// 'LM'
// 'LCM'
// 'CM'
// 'RCM'
// 'RM'
// 'LCDM'
// 'CDM'
// 'RCDM'
// 'LWB'
// 'LB'
// 'LCB
// 'CB'
// 'RCB'
// 'RB'
// 'RWB'
// 'GK'

const widePositionToCentralCounterPart: Partial<Record<PlatformPosition, PlatformPosition>> = {
  'LS': 'S',
  'RS': 'S',

  'LCAM': 'CAM',
  'RCAM': 'CAM',

  'LCM': 'CM',
  'RCM': 'CM',

  'LCDM': 'CDM',
  'RCDM': 'CDM',

  'LCB': 'CB',
  'RCB': 'CB',
};

const centralPositionToWideCounterParts: Partial<Record<PlatformPosition, PlatformPosition[]>> = {
  'S': ['LS', 'RS'],
  'CAM': ['LCAM', 'RCAM'],
  'CM': ['LCM', 'RCM'],
  'CDM': ['LCDM', 'RCDM'],
  'CB': ['LCB', 'RCB'],
};

const widePositionsToClosePositions: Partial<Record<PlatformPosition, PlatformPosition[]>> = {
  'LW': ['LCAM', 'LM', 'LS', 'LWB'],
  'RW': ['RCAM', 'RM', 'RS', 'RWB'],

  'LM': ['LW', 'LCAM', 'LWB'],
  'RM': ['RW', 'RCAM', 'RWB'],

  'LWB': ['LB', 'LM', 'LW'],
  'RWB': ['RB', 'RM', 'RW'],

  'LB': ['LWB', 'LM'],
  'RB': ['RWB', 'RM'],
};


// get players (currently based on current formation config) that align with the new formation config
export const getPlayersWithNewFormation = (
  players: TeamPlayers,
  newFormation: FormationConfig,
  currentFormation: FormationConfig,
): TeamPlayers => {

  const newPlayers: TeamPlayers = {};

  Object.entries(currentFormation).forEach(([position, coordinate]) => {

    const playersToMove = players[position] ?? [];

    if (playersToMove.length > 0) {

      // (1) if the position exists in the new formation, we keep the players in this position
      if (newFormation[position as PlatformPosition]) {
        newPlayers[position] = [...(newPlayers[position] ?? []), ...playersToMove];
        return;
      }


      // (2) if the position is a wide position with a central counter part, we move the players to the central counter part
      const centralCounterPart = widePositionToCentralCounterPart[position as PlatformPosition];
      if (centralCounterPart && newFormation[centralCounterPart]) {
        newPlayers[centralCounterPart] = [...(newPlayers[centralCounterPart] ?? []), ...playersToMove];
        return;
      }


      // (3) if the position is a central position with wide counter part(s), we move the players to the wide counter part(s)
      const wideCounterParts = (centralPositionToWideCounterParts[position as PlatformPosition] ?? [])
        .filter(counterPart => newFormation[counterPart as PlatformPosition]);

      if (wideCounterParts.length > 0) {
        // if we have two counter parts, we alternate between them
        if (wideCounterParts.length === 2) {
          playersToMove.forEach((player, index) => {
            newPlayers[wideCounterParts[index % 2]] = [...(newPlayers[wideCounterParts[index % 2]] ?? []), player];
          });
          return;
        }

        // if we have one counter part, we add all players to it
        if (wideCounterParts.length === 1) {
          newPlayers[wideCounterParts[0]] = [...(newPlayers[wideCounterParts[0]] ?? []), ...playersToMove];
          return;
        }
      }


      // (4) if the position is a wide position with a close wide position, we move the players to the closest wide position
      const closePositions = widePositionsToClosePositions[position as PlatformPosition] ?? [];
      if (closePositions.length > 0) {
        for (const closePosition of closePositions) {
          if (newFormation[closePosition as PlatformPosition]) {
            newPlayers[closePosition] = [...(newPlayers[closePosition] ?? []), ...playersToMove];
            return;
          }
        }
      }

      // (5) closest positions in space
      const closestPositionsInSpace = getClosestPositions(coordinate, newFormation);
      if (closestPositionsInSpace.length > 0) {
        playersToMove.forEach((player, index) => {
          newPlayers[closestPositionsInSpace[index % closestPositionsInSpace.length]] = [
            ...(newPlayers[closestPositionsInSpace[index % closestPositionsInSpace.length]] ?? []),
            player,
          ];
        });
        return;
      }

      // (6) fallback to the first position in the new formation
      const firstPositionInNewFormation = Object.keys(newFormation)[0] as PlatformPosition;
      newPlayers[firstPositionInNewFormation] = [...(newPlayers[firstPositionInNewFormation] ?? []), ...playersToMove];
    }
  });

  return newPlayers;
};


// get the closest positions to a given coordinate in a formation config (excluding goalkeeper)
const getClosestPositions = (
  coordinate: PositionCoordinates,
  formationConfig: FormationConfig,
): PlatformPosition[] => {

  // calculate distances for all positions
  let distances = Object.entries(formationConfig).map(([position, positionCoordinate]) => ({
    position: position as PlatformPosition,
    distance: Math.sqrt(
      Math.pow(positionCoordinate.x - coordinate.x, 2) +
      Math.pow(positionCoordinate.y - coordinate.y, 2)
    )
  }));

  distances = distances.filter(d => d.position !== 'GK');

  // find the minimum distance
  const minDistance = Math.min(...distances.map(d => d.distance));

  // return all positions that have the minimum distance
  return distances
    .filter(d => d.distance === minDistance)
    .map(d => d.position);
};
